import type { PayloadSnapshot, Row, ViewState } from './types'
import { compareRows } from './sorting'
import { netInWindow } from './trend'

export interface Decision {
  id: 'growth' | 'unassigned' | 'blocker' | 'programs'
  title: string
  fact: string
  ask: string
  accountable: string[]
  walk: Partial<Pick<ViewState, 'owner' | 'pri' | 'due' | 'scope'>> | null
}

function topAreas(rows: Row[], measure: (rows: Row[]) => number, limit = 3): Array<{ area: string, count: number }> {
  const groups = new Map<string, Row[]>()
  rows.forEach((row) => {
    const group = groups.get(row.svp) || []
    group.push(row)
    groups.set(row.svp, group)
  })
  return Array.from(groups.entries())
    .map(([area, areaRows]) => ({ area, count: measure(areaRows) }))
    .filter((entry) => entry.count > 0)
    .sort((left, right) => right.count - left.count || left.area.localeCompare(right.area))
    .slice(0, limit)
}

export function addressee(area: string, payload: Pick<PayloadSnapshot, 'unowned_group' | 'svp_lead'>): string {
  if (area === payload.unowned_group) return 'unassigned: these items sit in projects with no confirmed platform area owner'
  return payload.svp_lead[area] ? `${payload.svp_lead[area]} (${area})` : `${area}, which has no SVP owner recorded`
}

export function decisions(
  rows: Row[],
  state: Pick<ViewState, 'area' | 'win'>,
  payload: Pick<PayloadSnapshot, 'as_of' | 'unowned_group' | 'svp_lead'>,
): Decision[] {
  const open = rows.filter((row) => row.open)
  const oneArea = state.area !== 'ALL'
  const net = netInWindow(rows, state.win, payload.as_of)
  const growing = topAreas(rows, (areaRows) => netInWindow(areaRows, state.win, payload.as_of).net)
  const growthOwners = oneArea ? [state.area] : growing.map((entry) => entry.area)

  const unassigned = open.filter((row) => !row.assignee)
  const unassignedAreas = topAreas(open, (areaRows) => areaRows.filter((row) => !row.assignee).length)

  const p1 = open.filter((row) => row.pri_rank === 0).slice().sort(compareRows('worst'))
  const p1Areas = topAreas(open, (areaRows) => areaRows.filter((row) => row.pri_rank === 0).length)

  const overdue = open.filter((row) => row.due_status === 'overdue')
  const programs = new Map<string, Row[]>()
  overdue.forEach((row) => {
    const key = row.epic || 'No initiative'
    const entries = programs.get(key) || []
    entries.push(row)
    programs.set(key, entries)
  })
  const dominant = Array.from(programs.entries())
    .map(([name, programRows]) => ({ name, rows: programRows, count: programRows.length }))
    .filter((program) => program.count > 1)
    .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
    .slice(0, 3)
  const programOwners = oneArea
    ? [state.area]
    : Array.from(new Set(dominant.reduce<string[]>((areas, program) => {
      program.rows.forEach((row) => areas.push(row.svp))
      return areas
    }, []))).slice(0, 3)

  return [
    {
      id: 'growth',
      title: 'Backlog growth',
      // A net of exactly 0 is flat, not a reduction. verdictOf() and TrendPanel already call this
      // "stable"; claiming "the backlog shrank" here put two panels in conflict on one screen.
      fact: net.net > 0
        ? `${net.opened} risks opened against ${net.resolved} resolved, a net of +${net.net}.`
        : net.net < 0
          ? `${net.resolved} risks resolved against ${net.opened} opened, a net of ${net.net}. The backlog shrank.`
          : `${net.opened} risks opened against ${net.resolved} resolved, a net of 0. The backlog held flat.`,
      ask: net.net > 0
        ? 'Decision: the resolution rate committed to for next quarter, and what is deprioritised to fund it.'
        : 'Decision: what produced this result, and whether it holds through next quarter.',
      accountable: growthOwners.map((area) => addressee(area, payload)),
      walk: null,
    },
    {
      id: 'unassigned',
      title: 'Unassigned backlog',
      fact: unassigned.length
        ? `${unassigned.length} of ${open.length} open items are unassigned. These are not items without an owning team; they are items not yet scheduled into active work.`
        : 'Every open item in this view is assigned.',
      ask: unassigned.length
        ? 'Decision: which of these are scheduled this quarter, and which are formally deferred.'
        : 'No decision required this month.',
      accountable: (oneArea ? [state.area] : unassignedAreas.map((entry) => entry.area)).map((area) => addressee(area, payload)),
      walk: unassigned.length ? { owner: 'unassigned', scope: 'open' } : null,
    },
    {
      id: 'blocker',
      title: 'Open Blocker and P1 exposure',
      fact: p1.length
        ? `${p1.length} open items are Blocker or P1; ${p1.filter((row) => row.due_status === 'overdue').length} are overdue and ${p1.filter((row) => !row.assignee).length} have no owner.`
        : 'No open item in this view is at Blocker or P1.',
      ask: p1.length
        ? 'Decision: confirm each is in active work with a named owner, or reset the priority to match the plan.'
        : 'No decision required this month.',
      accountable: (oneArea ? [state.area] : p1Areas.map((entry) => entry.area)).map((area) => addressee(area, payload)),
      walk: p1.length ? { pri: 'p1', scope: 'open' } : null,
    },
    {
      id: 'programs',
      title: 'Dominant overdue programs',
      fact: !overdue.length
        ? 'Nothing in this view is overdue.'
        : dominant.length
          ? `${overdue.length} open items are overdue and the leading initiatives hold ${dominant.reduce((sum, entry) => sum + entry.count, 0)} of them.`
          : `${overdue.length} open items are overdue and no initiative holds more than one, so the exposure is spread thin.`,
      ask: !overdue.length
        ? 'No decision required this month.'
        : dominant.length
          ? 'Decision: the owner for unblocking each initiative, and whether the scope or the dates are revised.'
          : 'Decision: no initiative concentrates this overdue, so the question is whether these dates were ever committed.',
      accountable: (programOwners.length ? programOwners : oneArea ? [state.area] : Array.from(new Set(overdue.map((row) => row.svp))).slice(0, 3))
        .map((area) => addressee(area, payload)),
      walk: overdue.length ? { due: 'overdue', scope: 'open' } : null,
    },
  ]
}

export function decisionsText(items: Decision[], asOf: string, view: string): string {
  const lines = [`Risk register decisions, ${asOf}`, `View: ${view}`, '']
  items.forEach((item) => lines.push(item.title, item.fact, item.ask, `Accountable: ${item.accountable.join('; ')}`, ''))
  return lines.join('\n')
}
